import {
  formatAccountLabel,
  type NormalBalanceChoice,
  type NormalBalanceChoiceConflict,
  type NormalBalanceSide,
} from '@releve/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * The "manual normal-balance override" prompt (Clay's ruling 2026-10-06):
 * when a reclassification would replace a side someone chose by hand, the
 * user decides — keep it, or use the standard side of the new classification.
 * One prompt for both callers: the account edit form (answering the PATCH
 * route's 409 NORMAL_BALANCE_CHOICE_REQUIRED) and the super-admin template
 * editor (which detects the case client-side with `resolveReclassifiedSide`,
 * @releve/shared).
 * Opened through `useNormalBalanceChoice()` (normal-balance-choice.tsx).
 * docs/CHART_OF_ACCOUNTS.md "Contra subtypes".
 */

export interface NormalBalanceChoiceDialogProps {
  conflicts: NormalBalanceChoiceConflict[];
  showAccountNumbers: boolean;
  /** `null` = cancelled. */
  onChoose: (choice: NormalBalanceChoice | null) => void;
}

export function NormalBalanceChoiceDialog({ conflicts, showAccountNumbers, onChoose }: NormalBalanceChoiceDialogProps) {
  const { t } = useTranslation();
  const sideLabel = (side: NormalBalanceSide) => t(`chartOfAccountsForm.options.${side}`);
  // One current side across the list → name it on the buttons ("Keep Debit");
  // a mixed subtree gets the plural labels.
  const currents = new Set(conflicts.map((c) => c.current));
  const single = currents.size === 1 ? conflicts[0] : undefined;

  return (
    <AlertDialog open onOpenChange={(open) => !open && onChoose(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('chartOfAccountsForm.normalBalanceChoice.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('chartOfAccountsForm.normalBalanceChoice.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {conflicts.map((conflict) => (
            <li key={conflict.id}>
              {t('chartOfAccountsForm.normalBalanceChoice.line', {
                name: formatAccountLabel(conflict, showAccountNumbers),
                current: sideLabel(conflict.current),
                standard: sideLabel(conflict.standard),
              })}
            </li>
          ))}
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('chartOfAccountsForm.normalBalanceChoice.cancel')}</AlertDialogCancel>
          <Button variant="outline" onClick={() => onChoose('keep')}>
            {single
              ? t('chartOfAccountsForm.normalBalanceChoice.keep', { side: sideLabel(single.current) })
              : t('chartOfAccountsForm.normalBalanceChoice.keepMixed')}
          </Button>
          <AlertDialogAction onClick={() => onChoose('reset')}>
            {single
              ? t('chartOfAccountsForm.normalBalanceChoice.reset', { side: sideLabel(single.standard) })
              : t('chartOfAccountsForm.normalBalanceChoice.resetMixed')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
